/**
 * Derives every analysis payload from the generated transactions, so the mock
 * backend stays internally consistent: a recommendation's evidence always
 * names transactions, series and accounts that exist.
 *
 * The scoring maths follows health-score.schema.json `x-scoring` exactly — the
 * same definitions the Server surface implements.
 */
import categorySchema from '@/generated/product/data/category.schema.json'
import type {
  Account,
  CashFlow,
  CashFlowMonth,
  CategoryId,
  DebtPlan,
  FeeGroup,
  HealthScore,
  HealthScorePillar,
  Money,
  Progress,
  Recommendation,
  RecurringSeries,
  Transaction,
} from '@/generated/types'
import type { DebtSummary, FeeKind } from '../api'
import { CURRENCY, money, recurringHints, uuid, type Dataset } from './dataset'

type CategoryKind = 'income' | 'essential' | 'discretionary' | 'debt' | 'savings' | 'neutral'
const taxonomy = categorySchema['x-taxonomy'] as Record<CategoryId, CategoryKind>

const monthKey = (iso: string) => iso.slice(0, 7)
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
/** Linear 0-100 between a floor that scores 0 and a ceiling that scores 100. */
const scale = (value: number, zeroAt: number, hundredAt: number) =>
  Math.round(clamp((value - zeroAt) / (hundredAt - zeroAt), 0, 1) * 100)

export function bandFor(score: number): 'poor' | 'fair' | 'good' | 'strong' {
  if (score >= 80) return 'strong'
  if (score >= 60) return 'good'
  if (score >= 40) return 'fair'
  return 'poor'
}

// ------------------------------------------------------------------ cash flow

export function deriveCashFlow(data: Dataset, months: number): CashFlow {
  const byMonth = new Map<string, Transaction[]>()
  for (const tx of data.transactions) {
    const key = monthKey(tx.date)
    const bucket = byMonth.get(key)
    if (bucket) bucket.push(tx)
    else byMonth.set(key, [tx])
  }

  const keys = [...byMonth.keys()].sort().slice(-months)
  const latest = keys[keys.length - 1]

  const categoryAverages = new Map<CategoryId, number[]>()

  const result: CashFlowMonth[] = keys.map((key) => {
    const rows = byMonth.get(key)!
    let income = 0
    let spending = 0
    let saved = 0
    const byCategory = new Map<CategoryId, { amount: number; count: number }>()

    for (const tx of rows) {
      const kind = taxonomy[tx.category]
      if (tx.category === 'savingsTransfer' && tx.amount.amount < 0) saved += -tx.amount.amount
      if (tx.isTransfer) continue
      if (tx.amount.amount > 0 && kind === 'income') {
        income += tx.amount.amount
        continue
      }
      if (tx.amount.amount < 0) {
        spending += tx.amount.amount
        const existing = byCategory.get(tx.category) ?? { amount: 0, count: 0 }
        existing.amount += tx.amount.amount
        existing.count += 1
        byCategory.set(tx.category, existing)
      }
    }

    for (const [category, entry] of byCategory) {
      const history = categoryAverages.get(category) ?? []
      history.push(Math.abs(entry.amount))
      categoryAverages.set(category, history)
    }

    return {
      month: key,
      income: money(income),
      spending: money(spending),
      net: money(income + spending),
      saved: money(saved),
      complete: key !== latest,
      byCategory: [...byCategory.entries()]
        .sort((a, b) => a[1].amount - b[1].amount)
        .map(([category, entry]) => {
          const history = (categoryAverages.get(category) ?? []).slice(-6)
          const average = history.reduce((s, v) => s + v, 0) / Math.max(history.length, 1)
          return {
            category,
            amount: money(entry.amount),
            transactionCount: entry.count,
            changeVsAverage: average > 0 ? Math.abs(entry.amount) / average - 1 : null,
          }
        }),
    }
  })

  const complete = result.filter((m) => m.complete)
  const mean = (pick: (m: CashFlowMonth) => number) =>
    complete.length ? Math.round(complete.reduce((s, m) => s + pick(m), 0) / complete.length) : 0

  const trailing = complete.slice(-3)
  const trailingIncome = trailing.reduce((s, m) => s + m.income.amount, 0)
  const trailingSaved = trailing.reduce((s, m) => s + (m.saved?.amount ?? 0), 0)
  const yearIncome = complete.reduce((s, m) => s + m.income.amount, 0)
  const yearSaved = complete.reduce((s, m) => s + (m.saved?.amount ?? 0), 0)

  const savingsAccount = data.accounts.find((a) => a.type === 'savings')
  const liquid = savingsAccount?.currentBalance?.amount ?? 0
  const averageSpending = mean((m) => Math.abs(m.spending.amount))

  const target = data.emergencyFundTarget ?? (averageSpending ? money(averageSpending * 3) : null)

  return {
    currency: CURRENCY,
    months: result,
    averageNet: money(mean((m) => m.net.amount)),
    averageIncome: money(mean((m) => m.income.amount)),
    averageSpending: money(mean((m) => m.spending.amount)),
    negativeMonthCount: complete.filter((m) => m.net.amount < 0).length,
    runwayMonths: averageSpending > 0 ? Number((liquid / averageSpending).toFixed(1)) : null,
    savingsRate: trailingIncome > 0 ? Number((trailingSaved / trailingIncome).toFixed(4)) : null,
    savingsRateAverage12m: yearIncome > 0 ? Number((yearSaved / yearIncome).toFixed(4)) : null,
    emergencyFund: target
      ? {
          current: money(liquid),
          target,
          monthsCovered: averageSpending > 0 ? Number((liquid / averageSpending).toFixed(1)) : null,
          reached: liquid >= target.amount,
        }
      : undefined,
  }
}

// ------------------------------------------------------------------ recurring

const cadenceFor = (medianDays: number): RecurringSeries['cadence'] => {
  if (medianDays <= 9) return 'weekly'
  if (medianDays <= 18) return 'biweekly'
  if (medianDays <= 45) return 'monthly'
  if (medianDays <= 120) return 'quarterly'
  if (medianDays <= 240) return 'semiannual'
  if (medianDays <= 400) return 'annual'
  return 'irregular'
}

const monthlyFactor: Record<RecurringSeries['cadence'], number> = {
  weekly: 52 / 12,
  biweekly: 26 / 12,
  monthly: 1,
  quarterly: 1 / 3,
  semiannual: 1 / 6,
  annual: 1 / 12,
  irregular: 1,
}

const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000)

const addDays = (iso: string, days: number) => new Date(Date.parse(iso) + days * 86_400_000).toISOString().slice(0, 10)

export function deriveRecurring(data: Dataset): RecurringSeries[] {
  const groups = new Map<string, Transaction[]>()
  for (const tx of data.transactions) {
    if (!tx.isRecurring || !tx.merchantKey) continue
    const bucket = groups.get(tx.merchantKey)
    if (bucket) bucket.push(tx)
    else groups.set(tx.merchantKey, [tx])
  }

  const series: RecurringSeries[] = []
  for (const [key, rowsDesc] of groups) {
    const rows = [...rowsDesc].sort((a, b) => (a.date < b.date ? -1 : 1))
    if (rows.length < 2) continue

    const gaps: number[] = []
    for (let i = 1; i < rows.length; i++) gaps.push(daysBetween(rows[i - 1].date, rows[i].date))
    gaps.sort((a, b) => a - b)
    const medianDays = gaps[Math.floor(gaps.length / 2)] || 30
    const cadence = cadenceFor(medianDays)

    const priceHistory: RecurringSeries['priceHistory'] = []
    for (const row of rows) {
      const last = priceHistory[priceHistory.length - 1]
      if (!last || last.amount.amount !== row.amount.amount) {
        priceHistory.push({ effectiveFrom: row.date, amount: row.amount })
      }
    }

    const first = priceHistory[0].amount.amount
    const current = priceHistory[priceHistory.length - 1].amount.amount
    const hint = recurringHints.get(key)
    const lastCharged = rows[rows.length - 1].date
    const typical = Math.abs(current)

    const status: RecurringSeries['status'] =
      priceHistory.length > 1 && Math.abs(current) > Math.abs(first)
        ? 'priceIncreased'
        : hint?.unused
          ? 'unused'
          : 'active'

    series.push({
      id: uuid(`series:${key}`),
      name: rows[0].merchant ?? key,
      merchantKey: key,
      accountId: rows[0].accountId,
      category: rows[0].category,
      cadence,
      cadenceDays: medianDays,
      typicalAmount: money(-typical),
      monthlyEquivalent: money(-Math.round(typical * monthlyFactor[cadence])),
      annualCost: money(-Math.round(typical * monthlyFactor[cadence] * 12)),
      paidToDate: money(rows.reduce((s, r) => s + r.amount.amount, 0)),
      firstSeen: rows[0].date,
      lastCharged,
      nextExpected: addDays(lastCharged, medianDays),
      occurrenceCount: rows.length,
      confidence: Math.min(0.99, 0.6 + rows.length * 0.04),
      status,
      priceHistory,
      priceChangePercent: first !== 0 ? Number((Math.abs(current) / Math.abs(first) - 1).toFixed(3)) : null,
      overlapsWith: hint?.duplicateOf ? [uuid(`series:${hint.duplicateOf}`)] : [],
      transactionIds: rows.map((r) => r.id),
      userConfirmed: false,
    })
  }

  return series.sort((a, b) => (a.monthlyEquivalent?.amount ?? 0) - (b.monthlyEquivalent?.amount ?? 0))
}

// ----------------------------------------------------------------------- fees

const AVOIDABLE: FeeKind[] = [
  'overdraft',
  'nsf',
  'atm',
  'latePayment',
  'monthlyMaintenance',
  'foreignTransaction',
]

export function deriveFees(data: Dataset, months: number): FeeGroup[] {
  const cutoff = cutoffMonth(data, months)
  const thisMonth = latestMonth(data)
  const groups = new Map<string, Transaction[]>()

  for (const tx of data.transactions) {
    if (!tx.isFee || monthKey(tx.date) < cutoff) continue
    const kind = tx.feeKind ?? 'other'
    const bucket = groups.get(kind)
    if (bucket) bucket.push(tx)
    else groups.set(kind, [tx])
  }

  return [...groups.entries()]
    .map(([kind, rows]) => {
      const sorted = [...rows].sort((a, b) => (a.date < b.date ? -1 : 1))
      const total = rows.reduce((s, r) => s + r.amount.amount, 0)
      const current = rows
        .filter((r) => monthKey(r.date) === thisMonth)
        .reduce((s, r) => s + r.amount.amount, 0)
      const half = Math.floor(sorted.length / 2)
      const older = sorted.slice(0, half).reduce((s, r) => s + Math.abs(r.amount.amount), 0)
      const newer = sorted.slice(half).reduce((s, r) => s + Math.abs(r.amount.amount), 0)
      return {
        kind: kind as FeeKind,
        total: money(total),
        totalThisMonth: money(current),
        count: rows.length,
        avoidable: AVOIDABLE.includes(kind as FeeKind),
        accountIds: [...new Set(rows.map((r) => r.accountId))],
        transactionIds: rows.map((r) => r.id),
        firstSeen: sorted[0].date,
        lastSeen: sorted[sorted.length - 1].date,
        trend: newer > older * 1.15 ? 'rising' : newer < older * 0.85 ? 'falling' : 'flat',
      } satisfies FeeGroup
    })
    .sort((a, b) => a.total.amount - b.total.amount)
}

function latestMonth(data: Dataset): string {
  return data.transactions.length ? monthKey(data.transactions[0].date) : ''
}

function cutoffMonth(data: Dataset, months: number): string {
  const all = [...new Set(data.transactions.map((t) => monthKey(t.date)))].sort()
  return all[Math.max(0, all.length - months)] ?? ''
}

// ----------------------------------------------------------------------- debt

export function deriveDebt(data: Dataset): DebtSummary {
  const debts = data.accounts.filter((a) => a.isDebt)
  const totalOwed = debts.reduce((s, a) => s + Math.abs(a.currentBalance?.amount ?? 0), 0)
  const totalLimit = debts.reduce((s, a) => s + (a.creditLimit?.amount ?? 0), 0)
  const monthlyInterest = debts.reduce(
    (s, a) => s + Math.round((Math.abs(a.currentBalance?.amount ?? 0) * (a.aprBasisPoints ?? 0)) / 10000 / 12),
    0,
  )
  const weightedApr = totalOwed
    ? Math.round(
        debts.reduce((s, a) => s + Math.abs(a.currentBalance?.amount ?? 0) * (a.aprBasisPoints ?? 0), 0) /
          totalOwed,
      )
    : null

  return {
    accounts: debts,
    totalOwed: money(totalOwed),
    monthlyInterest: money(-monthlyInterest),
    utilization: totalLimit > 0 ? Number((totalOwed / totalLimit).toFixed(4)) : null,
    averageAprBasisPoints: weightedApr,
  }
}

interface SimDebt {
  account: Account
  balance: number
  apr: number
  minimum: number
}

function simulate(debts: SimDebt[], monthlyPayment: number, strategy: DebtPlan['strategy'], start: Date) {
  const order = [...debts].sort((a, b) =>
    strategy === 'snowball' ? a.balance - b.balance : b.apr - a.apr,
  )
  const balances = new Map(order.map((d) => [d.account.id, d.balance]))
  const interestPaid = new Map(order.map((d) => [d.account.id, 0]))
  const payoffDate = new Map<string, string>()
  const schedule: NonNullable<DebtPlan['schedule']> = []

  let totalInterest = 0
  let monthIndex = 0
  const maxMonths = 600

  while ([...balances.values()].some((b) => b > 0) && monthIndex < maxMonths) {
    const date = new Date(start.getFullYear(), start.getMonth() + monthIndex + 1, 1)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    let budget = monthlyPayment
    let interestThisMonth = 0
    let principalThisMonth = 0

    for (const debt of order) {
      const balance = balances.get(debt.account.id)!
      if (balance <= 0) continue
      const interest = Math.round((balance * debt.apr) / 10000 / 12)
      balances.set(debt.account.id, balance + interest)
      interestPaid.set(debt.account.id, interestPaid.get(debt.account.id)! + interest)
      interestThisMonth += interest
      totalInterest += interest
    }

    // Minimums first, then everything left over goes at the head of the order.
    for (const debt of order) {
      const balance = balances.get(debt.account.id)!
      if (balance <= 0 || budget <= 0) continue
      const pay = Math.min(balance, Math.min(debt.minimum, budget))
      balances.set(debt.account.id, balance - pay)
      budget -= pay
      principalThisMonth += pay
    }
    for (const debt of order) {
      const balance = balances.get(debt.account.id)!
      if (balance <= 0 || budget <= 0) continue
      const pay = Math.min(balance, budget)
      balances.set(debt.account.id, balance - pay)
      budget -= pay
      principalThisMonth += pay
    }

    for (const debt of order) {
      if (balances.get(debt.account.id)! <= 0 && !payoffDate.has(debt.account.id)) {
        payoffDate.set(debt.account.id, `${key}-28`)
      }
    }

    schedule.push({
      month: key,
      remaining: money([...balances.values()].reduce((s, b) => s + Math.max(0, b), 0)),
      interest: money(-interestThisMonth),
      principal: money(-principalThisMonth),
    })
    monthIndex++
  }

  return { schedule, totalInterest, months: monthIndex, order, interestPaid, payoffDate }
}

export function buildPlan(
  data: Dataset,
  monthlyPayment: Money,
  strategy: DebtPlan['strategy'],
  now = new Date(),
): DebtPlan {
  const debts: SimDebt[] = data.accounts
    .filter((a) => a.isDebt && (a.currentBalance?.amount ?? 0) !== 0)
    .map((account) => ({
      account,
      balance: Math.abs(account.currentBalance?.amount ?? 0),
      apr: account.aprBasisPoints ?? 1800,
      minimum: account.minimumPayment?.amount ?? 2500,
    }))

  const minimumTotal = debts.reduce((s, d) => s + d.minimum, 0)
  const totalOwed = debts.reduce((s, d) => s + d.balance, 0)
  const effective = strategy === 'minimum' ? minimumTotal : Math.max(monthlyPayment.amount, minimumTotal)

  const run = simulate(debts, effective, strategy, now)
  const baseline = simulate(debts, minimumTotal, 'minimum', now)

  const last = run.schedule[run.schedule.length - 1]
  const payoffDate = last ? `${last.month}-28` : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-28`

  return {
    strategy,
    monthlyPayment: money(effective),
    minimumPaymentTotal: money(minimumTotal),
    totalOwed: money(totalOwed),
    totalInterest: money(-run.totalInterest),
    interestSavedVsMinimum: money(Math.max(0, baseline.totalInterest - run.totalInterest)),
    monthsSavedVsMinimum: Math.max(0, baseline.months - run.months),
    payoffDate,
    monthsToPayoff: run.months,
    averageAprBasisPoints: deriveDebt(data).averageAprBasisPoints,
    utilization: deriveDebt(data).utilization,
    monthlyInterestNow: deriveDebt(data).monthlyInterest,
    debts: run.order.map((debt, index) => ({
      accountId: debt.account.id,
      name: debt.account.name,
      balance: money(debt.balance),
      aprBasisPoints: debt.account.aprBasisPoints ?? null,
      aprSource: debt.account.aprBasisPoints ? 'statement' : 'estimated',
      minimumPayment: money(debt.minimum),
      creditLimit: debt.account.creditLimit,
      utilization: debt.account.creditLimit?.amount
        ? Number((debt.balance / debt.account.creditLimit.amount).toFixed(4))
        : null,
      order: index + 1,
      payoffDate: run.payoffDate.get(debt.account.id) ?? payoffDate,
      interestPaid: money(-(run.interestPaid.get(debt.account.id) ?? 0)),
    })),
    schedule: run.schedule,
    isSaved: false,
  }
}

// --------------------------------------------------------------- health score

const WEIGHTS: Record<HealthScorePillar['id'], number> = {
  cashFlow: 0.25,
  debtLoad: 0.25,
  savingsRate: 0.2,
  spendingStability: 0.15,
  feesAndWaste: 0.15,
}

const DETAIL_SCREEN: Record<HealthScorePillar['id'], NonNullable<HealthScorePillar['detailScreen']>> = {
  cashFlow: 'cashFlow',
  savingsRate: 'savings',
  debtLoad: 'debt',
  spendingStability: 'categories',
  feesAndWaste: 'fees',
}

export function deriveScore(
  data: Dataset,
  cashFlow: CashFlow,
  fees: FeeGroup[],
  recurring: RecurringSeries[],
  now = new Date(),
): HealthScore {
  const complete = cashFlow.months.filter((m) => m.complete)
  const monthsOfData = complete.length

  const meanNet = complete.length ? complete.reduce((s, m) => s + m.net.amount, 0) / complete.length : 0
  const meanIncome = complete.length ? complete.reduce((s, m) => s + m.income.amount, 0) / complete.length : 0
  const cashFlowRatio = meanIncome > 0 ? meanNet / meanIncome : 0

  const savingsRatio = cashFlow.savingsRate ?? 0

  const debt = deriveDebt(data)
  const utilization = debt.utilization ?? 0
  const debtPayments = complete.length
    ? complete.reduce(
        (s, m) =>
          s + Math.abs(m.byCategory?.find((c) => c.category === 'debtPayment')?.amount.amount ?? 0),
        0,
      ) / complete.length
    : 0
  const paymentRatio = meanIncome > 0 ? debtPayments / meanIncome : 0
  const debtScore =
    debt.totalOwed.amount === 0
      ? 100
      : Math.round(
          0.6 * scale(utilization, 0.8, 0.1) + 0.4 * scale(paymentRatio, 0.45, 0.1),
        )

  const discretionary = complete.map((m) =>
    Math.abs(
      (m.byCategory ?? [])
        .filter((c) => taxonomy[c.category] === 'discretionary')
        .reduce((s, c) => s + c.amount.amount, 0),
    ),
  )
  const dMean = discretionary.length ? discretionary.reduce((s, v) => s + v, 0) / discretionary.length : 0
  const dSd = dMean
    ? Math.sqrt(discretionary.reduce((s, v) => s + (v - dMean) ** 2, 0) / discretionary.length)
    : 0
  const cv = dMean > 0 ? dSd / dMean : 0

  const annualFees = Math.abs(fees.reduce((s, f) => s + f.total.amount, 0))
  const wasted = recurring
    .filter((s) => s.status === 'unused' || (s.overlapsWith?.length ?? 0) > 0)
    .reduce((s, r) => s + Math.abs(r.annualCost?.amount ?? 0), 0)
  const annualIncome = meanIncome * 12
  const wasteRatio = annualIncome > 0 ? (annualFees + wasted) / annualIncome : 0

  const raw: Record<HealthScorePillar['id'], { score: number; value: number; target: number }> = {
    cashFlow: { score: scale(cashFlowRatio, 0, 0.2), value: cashFlowRatio, target: 0.2 },
    savingsRate: { score: scale(savingsRatio, 0, 0.2), value: savingsRatio, target: 0.2 },
    debtLoad: { score: debtScore, value: utilization, target: 0.1 },
    spendingStability: { score: scale(cv, 0.6, 0.1), value: cv, target: 0.1 },
    feesAndWaste: { score: scale(wasteRatio, 0.03, 0), value: wasteRatio, target: 0 },
  }

  const pillars: HealthScorePillar[] = (Object.keys(WEIGHTS) as HealthScorePillar['id'][]).map((id) => ({
    id,
    score: raw[id].score,
    weight: WEIGHTS[id],
    band: bandFor(raw[id].score),
    delta: id === 'feesAndWaste' ? 4 : id === 'debtLoad' ? -2 : 3,
    value: Number(raw[id].value.toFixed(4)),
    target: raw[id].target,
    detailScreen: DETAIL_SCREEN[id],
  }))

  const score = Math.round(pillars.reduce((s, p) => s + p.score * p.weight, 0))
  const previousScore = Math.max(0, score - 6)
  const periodEnd = complete.length
    ? `${complete[complete.length - 1].month}-28`
    : `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`

  return {
    score,
    band: bandFor(score),
    previousScore,
    delta: score - previousScore,
    deltaPeriod: 'threeMonths',
    pillars,
    topLevers: [],
    confidence: monthsOfData >= 6 ? 'high' : monthsOfData >= 3 ? 'medium' : 'low',
    monthsOfData,
    periodEnd,
    computedAt: now.toISOString(),
  }
}

// ------------------------------------------------------------ recommendations

export function deriveRecommendations(
  data: Dataset,
  cashFlow: CashFlow,
  fees: FeeGroup[],
  recurring: RecurringSeries[],
): Recommendation[] {
  const out: Recommendation[] = []
  const createdAt = new Date(Date.parse(`${cashFlow.months[cashFlow.months.length - 1]?.month ?? '2026-01'}-05`)).toISOString()

  const add = (
    kind: Recommendation['kind'],
    theme: NonNullable<Recommendation['theme']>,
    priority: number,
    effort: Recommendation['effort'],
    params: Record<string, unknown>,
    monthly: number,
    steps: string[],
    evidence: Recommendation['evidence'],
    scoreGain: number,
    oneTime = 0,
  ) => {
    out.push({
      id: uuid(`rec:${kind}:${JSON.stringify(params)}`),
      kind,
      copyKey: `recommendation.${kind}`,
      params: params as Recommendation['params'],
      theme,
      priority,
      monthlyImpact: money(monthly),
      oneTimeImpact: money(oneTime),
      annualImpact: money(monthly * 12 + oneTime),
      scoreGain,
      effort,
      steps,
      evidence,
      status: 'open',
      statusChangedAt: null,
      createdAt,
    })
  }

  for (const series of recurring.filter((s) => s.status === 'unused')) {
    add(
      'cancelSubscription',
      'subscriptions',
      92,
      'low',
      { name: series.name, amount: series.typicalAmount, cadence: series.cadence, date: series.lastCharged },
      Math.abs(series.monthlyEquivalent?.amount ?? 0),
      [
        `Open your account with ${series.name}.`,
        'Cancel the plan and keep the confirmation email.',
        'Check next month’s statement to confirm it stopped.',
      ],
      { recurringSeriesIds: [series.id], transactionIds: series.transactionIds.slice(0, 4) },
      3,
    )
  }

  for (const series of recurring.filter((s) => (s.overlapsWith?.length ?? 0) > 0)) {
    add(
      'duplicateSubscription',
      'subscriptions',
      88,
      'low',
      { name: series.name },
      Math.abs(series.monthlyEquivalent?.amount ?? 0),
      ['Compare what each plan actually gives you.', 'Keep one, cancel the other.'],
      { recurringSeriesIds: [series.id, ...(series.overlapsWith ?? [])] },
      2,
    )
  }

  for (const series of recurring.filter((s) => s.status === 'priceIncreased')) {
    const first = series.priceHistory?.[0]
    const last = series.priceHistory?.[series.priceHistory.length - 1]
    add(
      'priceIncrease',
      'subscriptions',
      74,
      'low',
      { name: series.name, before: first?.amount, after: last?.amount, month: last?.effectiveFrom?.slice(0, 7) },
      Math.abs((last?.amount.amount ?? 0) - (first?.amount.amount ?? 0)),
      ['Check whether a cheaper tier covers what you use.', 'Ask for a retention rate before renewing.'],
      { recurringSeriesIds: [series.id], transactionIds: series.transactionIds.slice(0, 3) },
      1,
    )
  }

  const overdraft = fees.find((f) => f.kind === 'overdraft')
  if (overdraft) {
    add(
      'avoidOverdraft',
      'fees',
      96,
      'medium',
      { count: overdraft.count, amount: overdraft.total },
      Math.round(Math.abs(overdraft.total.amount) / 12),
      [
        'Turn on a low-balance alert.',
        'Link savings as overdraft cover.',
        'Move autopay dates to just after payday.',
      ],
      { transactionIds: overdraft.transactionIds?.slice(0, 5), feeKinds: ['overdraft'], accountIds: overdraft.accountIds },
      5,
    )
  }

  const atm = fees.find((f) => f.kind === 'atm')
  if (atm) {
    add(
      'avoidAtmFees',
      'fees',
      70,
      'low',
      { amount: atm.total },
      Math.round(Math.abs(atm.total.amount) / 12),
      ['Withdraw at in-network ATMs.', 'Get cash back at the register instead.'],
      { transactionIds: atm.transactionIds?.slice(0, 5), feeKinds: ['atm'] },
      2,
    )
  }

  const maintenance = fees.find((f) => f.kind === 'monthlyMaintenance')
  const checking = data.accounts.find((a) => a.type === 'checking')
  if (maintenance && checking) {
    add(
      'avoidMaintenanceFee',
      'fees',
      82,
      'low',
      { account: checking.name, amount: maintenance.totalThisMonth ?? maintenance.total },
      Math.round(Math.abs(maintenance.total.amount) / 12),
      ['Ask about a fee waiver for direct deposit.', 'Or switch to the no-fee account tier.'],
      { accountIds: [checking.id], transactionIds: maintenance.transactionIds?.slice(0, 3), feeKinds: ['monthlyMaintenance'] },
      3,
    )
  }

  const debt = deriveDebt(data)
  const worst = [...debt.accounts].sort((a, b) => (b.aprBasisPoints ?? 0) - (a.aprBasisPoints ?? 0))[0]
  if (worst) {
    const monthlyInterest = Math.round(
      (Math.abs(worst.currentBalance?.amount ?? 0) * (worst.aprBasisPoints ?? 0)) / 10000 / 12,
    )
    add(
      'payHighestApr',
      'debt',
      98,
      'medium',
      { account: worst.name, rate: worst.aprBasisPoints, amount: money(-monthlyInterest) },
      monthlyInterest,
      ['Pay the minimum on every other card.', 'Send everything spare at this balance.'],
      { accountIds: [worst.id] },
      7,
    )
  }

  const stretched = debt.accounts.find(
    (a) => (a.creditLimit?.amount ?? 0) > 0 && Math.abs(a.currentBalance?.amount ?? 0) / (a.creditLimit?.amount ?? 1) > 0.3,
  )
  if (stretched) {
    add(
      'reduceUtilization',
      'debt',
      66,
      'medium',
      {
        account: stretched.name,
        percent: Math.abs(stretched.currentBalance?.amount ?? 0) / (stretched.creditLimit?.amount ?? 1),
      },
      0,
      ['Pay the balance below 30% of the limit before the statement closes.'],
      { accountIds: [stretched.id] },
      4,
    )
  }

  const latest = cashFlow.months.filter((m) => m.complete).slice(-1)[0]
  const overspent = latest?.byCategory
    ?.filter((c) => taxonomy[c.category] === 'discretionary' && (c.changeVsAverage ?? 0) > 0.15)
    .sort((a, b) => a.amount.amount - b.amount.amount)[0]
  if (overspent) {
    add(
      'reduceCategory',
      'cashFlow',
      58,
      'medium',
      { category: overspent.category, amount: overspent.amount, percent: overspent.changeVsAverage },
      Math.round(Math.abs(overspent.amount.amount) * 0.15),
      ['Set a weekly cap for this category.', 'Review it again at the end of the month.'],
      { categories: [overspent.category] },
      2,
    )
  }

  const fund = cashFlow.emergencyFund
  if (fund && !fund.reached) {
    add(
      'buildEmergencyFund',
      'savings',
      64,
      'high',
      { months: fund.monthsCovered ?? 0 },
      25_000,
      ['Set a standing transfer for the day after payday.', 'Raise it whenever income goes up.'],
      { accountIds: data.accounts.filter((a) => a.type === 'savings').map((a) => a.id) },
      6,
    )
  }

  if ((cashFlow.negativeMonthCount ?? 0) > 0) {
    add(
      'smoothCashFlow',
      'cashFlow',
      54,
      'medium',
      { count: cashFlow.negativeMonthCount, total: cashFlow.months.filter((m) => m.complete).length },
      Math.abs(Math.round((cashFlow.averageNet?.amount ?? 0) * 0.1)),
      ['Move the biggest bills off the tight week.', 'Keep one month of expenses as a buffer.'],
      { categories: ['housing', 'utilities'] },
      3,
    )
  }

  return out.sort((a, b) => b.priority - a.priority || (a.monthlyImpact?.amount ?? 0) - (b.monthlyImpact?.amount ?? 0))
}

// ------------------------------------------------------------------- progress

const RANGE_MONTHS: Record<Progress['range'], number> = { '3m': 3, '6m': 6, '12m': 12, all: 24 }

export function deriveProgress(
  range: Progress['range'],
  score: HealthScore,
  cashFlow: CashFlow,
  recurring: RecurringSeries[],
  recommendations: Recommendation[],
  debt: DebtSummary,
): Progress {
  const months = cashFlow.months.filter((m) => m.complete).slice(-RANGE_MONTHS[range])
  const span = Math.max(months.length - 1, 1)

  const points = months.map((m, i) => {
    const value = Math.round(score.score - (span - i) * 1.6)
    const clamped = clamp(value, 0, 100)
    return {
      month: m.month,
      score: clamped,
      band: bandFor(clamped),
      netCashFlow: m.net,
      savingsRate: m.income.amount > 0 ? Number(((m.saved?.amount ?? 0) / m.income.amount).toFixed(4)) : null,
      totalDebt: debt.totalOwed,
      recurringMonthly: money(recurring.reduce((s, r) => s + (r.monthlyEquivalent?.amount ?? 0), 0)),
      feesPaid: money(
        (m.byCategory ?? []).find((c) => c.category === 'fees')?.amount.amount ?? 0,
      ),
    }
  })

  const done = recommendations.filter((r) => r.status === 'done')
  const savedToDate = done.reduce((s, r) => s + (r.monthlyImpact?.amount ?? 0), 0)

  const milestones: Progress['milestones'] = done.map((r) => ({
    id: uuid(`milestone:${r.id}`),
    kind: 'recommendationDone' as const,
    date: (r.statusChangedAt ?? r.createdAt ?? '').slice(0, 10) || (points[points.length - 1]?.month ?? '2026-01') + '-01',
    copyKey: 'progress.milestone.recommendationDone',
    params: { title: r.copyKey, ...(r.params as object) },
    impact: r.monthlyImpact,
    linkScreen: 'recommendationDetail' as const,
    linkId: r.id,
  }))

  if (points.length > 1) {
    const reached = points[points.length - 1]
    milestones.unshift({
      id: uuid(`milestone:band:${reached.band}`),
      kind: 'scoreBand',
      date: `${reached.month}-28`,
      copyKey: 'progress.milestone.scoreBand',
      params: { band: reached.band },
      linkScreen: 'healthScore',
      linkId: null,
    })
  }

  return {
    range,
    points,
    scoreChange: points.length ? points[points.length - 1].score - points[0].score : null,
    savedToDate: money(savedToDate),
    actionsTaken: done.length,
    milestones: milestones.sort((a, b) => (a.date < b.date ? 1 : -1)),
  }
}
